import { Injectable } from '@angular/core';

import { OfficeRoute } from '../constants/office-route.enum';
import { CapabilitiesService } from '../shared-context/capabilities.service';
import { AuthType, ClientStartupConfigService } from '../shared-context/client-startup-config.service';
import { ConsumerUserConsentGroup, UserConsentConfig, UserConsentConfigService } from '../shared-context/user-consent-config.service';
import { FeedbackService } from './feedback.service';
import { OfficeRouteService } from './office-route.service';

@Injectable({
    providedIn: 'root'
})
export class FloodgateService {
    private readonly floodgateInitPromise: Promise<void>;
    private readonly userConsentConfigPromise: Promise<UserConsentConfig> = null;

    constructor(
        private readonly capabilitiesService: CapabilitiesService,
        private readonly clientStartupConfigService: ClientStartupConfigService,
        private readonly feedbackService: FeedbackService,
        private readonly officeRouteService: OfficeRouteService,
        private readonly userConsentConfigService: UserConsentConfigService
    ) {
        if (!this.capabilitiesService.capabilities.showFloodgate) {
            return;
        }

        if (this.capabilitiesService.capabilities.userConsentCallEnabled && this.clientStartupConfigService.config.authType === AuthType.MSA && !this.userConsentConfigPromise) {
            this.userConsentConfigPromise = this.userConsentConfigService.getConfig();
        }

        this.floodgateInitPromise = new Promise<void>((resolve, reject) => {
            const onSuccess = (scriptInsertionDuration: number) => {
                const ocvTelemetryData = this.feedbackService.getOCVTelemetryData(scriptInsertionDuration.toString());
                OfficeBrowserFeedback.initOptions.applicationGroup = ocvTelemetryData.applicationGroup;
                OfficeBrowserFeedback.initOptions.telemetryGroup = ocvTelemetryData.telemetryGroup;
                OfficeBrowserFeedback.initOptions.webGroup = ocvTelemetryData.webGroup;
                OfficeBrowserFeedback.initOptions.appId = this.feedbackService.getOcvAppId();
                if (OfficeBrowserFeedback.floodgate.initOptions) {
                    OfficeBrowserFeedback.floodgate.initOptions.onSurveyActivatedCallback = { onSurveyActivated: launcher => this.activateSurvey(launcher) };
                }
                OfficeBrowserFeedback.floodgate.initialize().then(resolve);
            };

            if (this.userConsentConfigPromise) {
                this.userConsentConfigPromise.then(config => {
                    if (config.userConsent !== ConsumerUserConsentGroup.CanConsent) {
                        reject();
                    } else if (!this.feedbackService.addOCVScriptToPage(onSuccess)) {
                        reject();
                    }
                });
            } else if (!this.feedbackService.addOCVScriptToPage(onSuccess)) {
                reject();
            }
        });
    }

    public recordFloodgate(events: Array<string>) {
        if (!this.capabilitiesService.capabilities.showFloodgate) {
            return;
        }

        this.floodgateInitPromise.then(() => {
            OfficeBrowserFeedback.floodgate.start().then(() => {
                const floodgateEngineListener = OfficeBrowserFeedback.floodgate.getEngine().getActivityListener();
                events.forEach(e => floodgateEngineListener.logActivity(e));
                OfficeBrowserFeedback.floodgate.stop();
            });
        });
    }

    private activateSurvey(launcher: { launch: () => void }) {
        const currentRoute = this.officeRouteService.getRouteItemForCurrentLocation().route;

        // only show floodgate on Office.com pages
        if (currentRoute && (currentRoute === OfficeRoute.OfficeHome || currentRoute === OfficeRoute.Search || currentRoute === OfficeRoute.AppGallery)) {
            launcher.launch();
        }
    }
}
